"use client";

import { X } from "@app-foundation/icons/lucide";

import { useEffect, useId, useRef, useState } from "react";
import { onboardingDocuments, legacyOnboardingDocuments } from "../documents";
import { acceptedConsentChoice, acceptedConsentChoices, sensitiveInformation, ONBOARDING_CONSENT_VERSION, type ConsentChoices } from "../consent";
import { OnboardingConsentChoices } from "./OnboardingConsentChoices";
import { SignaturePad, type SignaturePadHandle } from "./SignaturePad";
import { GuidedSubmitButton } from "@/components/forms/GuidedSubmitButton";
import { useReadToEnd } from "@/features/service-information/useReadToEnd";
import { ScrollConsent } from "@/features/service-information/PrivacyConsent";

type Ready = { status: "unsigned"; attemptId: string; signerName: string; signerIdCard: string; testOnly?: true; retryAvailable?: boolean; choices?: ConsentChoices };
export function OnboardingSigningDialog({ initialDocument, signed, read, onRead, choices, onChoice, onSigned, onClose, preview, testOnly = false, consentReady = true }: {
  initialDocument: number; signed: boolean; read: Record<"agreement" | "sensitive", boolean>;
  onRead: (id: "agreement" | "sensitive", value: boolean) => void; choices: ConsentChoices;
  onChoice: (choices: ConsentChoices) => void; onSigned: (testOnly?: boolean) => void; testOnly?: boolean; consentReady?: boolean; onClose: () => void; preview?: { fail: boolean };
}) {
  const [index, setIndex] = useState(read.agreement ? initialDocument : 0);
  const [phase, setPhase] = useState<"read" | "sign" | "consent">("read");
  const [preparedAgreement, setPreparedAgreement] = useState<{ id: "agreement"; title: string; text: string; digest: string } | null>(null);
  const [agreementError, setAgreementError] = useState("");
  const { body, checkEnd, reachedEnd } = useReadToEnd(true, `${index}:${phase}:${preparedAgreement?.digest ?? "loading"}`);
  const [ready, setReady] = useState<Ready | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmed, setConfirmed] = useState(false);
  const [sensitiveConfirmed, setSensitiveConfirmed] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);
  // Freeze a submitted payload for retries. Editing after an uncertain result must
  // never create another legal record or replace a previously submitted signature.
  const submitted = useRef<object | null>(null);
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const pending = useRef(false);
  const dialog = useRef<HTMLDialogElement>(null);
  const pad = useRef<SignaturePadHandle>(null);
  const titleId = useId();
  const currentAgreement = preparedAgreement ?? (preview || testOnly ? onboardingDocuments[0] : null);
  const doc = index === 0 && currentAgreement ? currentAgreement : onboardingDocuments[index];
  useEffect(() => {
    if (preview || testOnly) return;
    const controller = new AbortController();
    void fetch("/api/onboarding?document=creator", { cache: "no-store", signal: controller.signal }).then(async response => {
      const value = await response.json();
      if (!response.ok) throw new Error(value.error || "协议读取失败，请重试。");
      setPreparedAgreement(value.renderedDigest ? { id: "agreement", title: value.document.title, text: value.document.blocks.map((block: { runs?: { text: string }[] }) => (block.runs ?? []).map(run => run.text).join("")).join("\n"), digest: value.renderedDigest } : { ...legacyOnboardingDocuments[0], digest: "legacy" });
    }).catch(reason => { if (!controller.signal.aborted) setAgreementError(reason instanceof Error ? reason.message : "协议读取失败，请重试。"); });
    return () => controller.abort();
  }, [preview, testOnly]);
  useEffect(() => {
    const el = dialog.current; el?.showModal();
    const overflow = document.body.style.overflow; document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = overflow; el?.close(); };
  }, []);
  async function status() {
    if (preview) return { status: "unsigned" as const, attemptId: "preview", signerName: "签署人", signerIdCard: "演示身份" };
    const response = await fetch("/api/onboarding/signing", { cache: "no-store" });
    const value = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(value.error || "网站签署服务更新中，请稍后重试。");
    if (value.status === "active") return { status: "active" as const };
    if (value.status === "test-signed" && value.testOnly === true) return { status: "test-signed" as const };
    if (value.status !== "unsigned" || typeof value.attemptId !== "string") throw new Error("签署状态暂时无法确认，请重试。");
    return value as Ready;
  }
  async function prepare() {
    if (pending.current) return;
    pending.current = true; setBusy(true); setError("");
    try {
      const value = await status();
      if (value.status === "active" || value.status === "test-signed") {
        if (!consentReady) { setPhase("consent"); return; }
        onSigned(value.status === "test-signed"); onClose(); return;
      }
      setReady(value); if (value.retryAvailable && value.choices) onChoice(value.choices); setPhase("sign");
    } catch (e) { setError(e instanceof Error ? e.message : "签署暂不可用，请重试。"); }
    finally { pending.current = false; setBusy(false); }
  }
  async function submit() {
    if (pending.current || !ready || !read.agreement || !read.sensitive || !confirmed || !sensitiveConfirmed || !acceptedConsentChoices(choices) || (!hasSignature && !ready.retryAvailable)) return;
    pending.current = true; setBusy(true); setError("");
    try {
      setHasSubmitted(true);
      let testResult = false;
      if (preview) {
        if (preview.fail) throw new Error("签署未完成，请重试。");
      } else {
        if (!submitted.current) submitted.current = { version: ONBOARDING_CONSENT_VERSION, agreementRead: read.agreement, sensitiveRead: read.sensitive, choices,
          attemptId: ready.attemptId, ...(ready.retryAvailable ? { resume: true } : { signatureImage: pad.current?.image() }), confirmed: true };
        const response = await fetch("/api/onboarding/signing", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(submitted.current) });
        const value = await response.json().catch(() => ({}));
        if (!response.ok && value.code === "INVALID_INPUT") { submitted.current = null; setHasSubmitted(false); }
        testResult = value.status === "test-signed" && value.testOnly === true;
        if (!response.ok || (value.status !== "active" && !testResult)) throw new Error(value.error || "签署结果尚未确认，请核对结果后重试。");
      }
      onSigned(testResult); onClose();
    } catch (e) { setError(e instanceof Error ? e.message : "签署结果尚未确认，请核对结果后重试。"); }
    finally { pending.current = false; setBusy(false); }
  }
  async function saveConsent() {
    if (pending.current || !read.agreement || !read.sensitive || !acceptedConsentChoices(choices)) return;
    pending.current = true; setBusy(true); setError("");
    try {
      const response = await fetch("/api/onboarding", { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({
        action: "consent", version: ONBOARDING_CONSENT_VERSION, agreementRead: read.agreement, sensitiveRead: read.sensitive, choices,
      }) });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || result.state?.consentReady !== true) throw new Error(result.error || "信息处理确认未保存，请重试。");
      onSigned(testOnly); onClose();
    } catch (cause) { setError(cause instanceof Error ? cause.message : "信息处理确认未保存，请重试。"); }
    finally { pending.current = false; setBusy(false); }
  }
  async function recheck() {
    if (pending.current) return;
    pending.current = true; setBusy(true); setError("");
    try { const value = await status(); if (value.status === "active" || value.status === "test-signed") { onSigned(value.status === "test-signed"); onClose(); } else setError("尚未确认签署成功，可用原签名重试。"); }
    catch (e) { setError(e instanceof Error ? e.message : "暂时无法核对，请稍后重试。"); }
    finally { pending.current = false; setBusy(false); }
  }
  const missingChoices = sensitiveInformation.filter(({ key }) => !acceptedConsentChoice(key, choices[key])).map(item => item.title);
  const signingBlockers = [
    ...(!hasSignature && !ready?.retryAvailable ? ["手写本人姓名"] : []),
    ...(!confirmed ? ["确认自愿签署协议"] : []),
    ...(!sensitiveConfirmed ? ["单独确认签署附件"] : []),
    ...(missingChoices.length ? [`确认${missingChoices.join("、")}`] : []),
  ];
  const blockedReason = busy ? "正在核对或保存，请稍候。" : !currentAgreement ? agreementError || "正在读取协议，请稍候。" : phase === "read" ? !reachedEnd ? "请滚动至底部，阅读完本份协议后才能继续。" : "" : signingBlockers.length ? `还需完成：${signingBlockers.join("；")}。` : "";
  const canConfirm = (hasSignature || ready?.retryAvailable) && confirmed && sensitiveConfirmed && acceptedConsentChoices(choices);
  return <dialog ref={dialog} aria-labelledby={titleId} className="rg-sign-dialog" onCancel={event => { event.preventDefault(); if (!busy) onClose(); }}>
    <header><h2 id={titleId}>{phase === "read" ? doc.title : phase === "consent" ? "确认信息处理" : testOnly ? "确认测试签署" : "确认签署"}</h2><button type="button" aria-label="关闭签署" disabled={busy} onClick={onClose}><X size={20} aria-hidden="true" /></button></header>
    <div ref={body} tabIndex={0} role="region" aria-label={phase === "read" ? `${doc.title}全文` : "签署信息"} className="rg-sign-body" onScroll={checkEnd}>
      {phase === "read" ? <>
        {signed && <p className="rg-notice">{testOnly ? "测试签署已完成，以下为协议正文。" : "协议已签署，以下为协议正文。"}</p>}
        {(index === 0 && !currentAgreement ? "" : doc.text).split("\n").map((line, i) => <p key={i} className="mb-3 text-sm leading-7">{line}</p>)}
      </> : phase === "consent" ? <OnboardingConsentChoices choices={choices} onChoice={onChoice} disabled={busy} /> : <>
        {ready?.testOnly && <p className="rg-notice">测试签署 · 不生成正式合同</p>}
        <p className="rg-notice">签署人：{ready?.signerName} · {ready?.signerIdCard}</p>
        <OnboardingConsentChoices choices={choices} onChoice={value => { onChoice(value); setConfirmed(false); setSensitiveConfirmed(false); }} disabled={busy || hasSubmitted || Boolean(ready?.retryAvailable)} />
        <div data-locked={!acceptedConsentChoices(choices)}><p className="mt-6 mb-3 font-medium">请手写本人姓名</p>
        {ready?.retryAvailable ? <p className="rg-notice">上次签名已保存，确认后继续生成原合同。</p> : <SignaturePad ref={pad} onChange={value => { setHasSignature(value); if (!value) { setConfirmed(false); setSensitiveConfirmed(false); } }} disabled={busy || hasSubmitted || !acceptedConsentChoices(choices)} />}</div>
        <div className="grid gap-3 mt-5 text-sm">
          <ScrollConsent agreed={confirmed} disabled={busy || hasSubmitted || !acceptedConsentChoices(choices) || (!hasSignature && !ready?.retryAvailable)}
            onChange={value => { setConfirmed(value); setSensitiveConfirmed(false); }} title={currentAgreement?.title ?? onboardingDocuments[0].title}
            confirmationText="我已阅读并自愿签署" confirmLabel="确认自愿签署">
            {(currentAgreement?.text ?? "").split("\n").map((line, i) => <p key={i} className="mb-3 text-sm leading-7">{line}</p>)}
          </ScrollConsent>
          <ScrollConsent agreed={sensitiveConfirmed} disabled={busy || hasSubmitted || !confirmed || !acceptedConsentChoices(choices)}
            onChange={setSensitiveConfirmed} title={onboardingDocuments[1].title}
            confirmationText="我单独同意并签署" confirmationSuffix="，将本次签名用于协议及附件。" confirmLabel="确认单独同意">
            {onboardingDocuments[1].text.split("\n").map((line, i) => <p key={i} className="mb-3 text-sm leading-7">{line}</p>)}
          </ScrollConsent>
        </div>
        {preview && <p className="rg-notice">模拟签署，不生成正式合同。</p>}
      </>}
    </div>
    <footer>
      {error && <p role="alert" className="rg-error">{error}</p>}
      {phase === "read" ? <>
        <GuidedSubmitButton type="button" reason={blockedReason} className="rg-primary" disabled={busy} onClick={() => {
          if (!reachedEnd) return;
          onRead(doc.id, true);
          const other = index === 0 ? 1 : 0;
          if (!read[onboardingDocuments[other].id]) setIndex(other);
          else if (signed) onClose();
          else void prepare();
        }}>{busy ? "正在核对…" : !read[onboardingDocuments[index === 0 ? 1 : 0].id] ? "继续阅读另一份协议" : signed ? <><span className="sr-only">关闭</span><X size={20} aria-hidden="true" className="inline-block" /></> : "阅读完成并签署"}</GuidedSubmitButton>
      </> : phase === "consent" ? <GuidedSubmitButton type="button" className="rg-primary" disabled={busy}
        reason={acceptedConsentChoices(choices) ? "" : "请按顺序逐项确认信息处理选择。"}
        onClick={() => void saveConsent()}>{busy ? "正在保存…" : "确认信息处理"}</GuidedSubmitButton> : <>
        <GuidedSubmitButton type="button" reason={!canConfirm ? blockedReason : ""} className="rg-primary" disabled={busy} onClick={() => void submit()}>{busy ? (testOnly ? "正在保存…" : "正在生成合同…") : hasSubmitted ? "重试签署" : testOnly ? "确认测试签署" : "确认签署协议及附件"}</GuidedSubmitButton>
        {hasSubmitted && !busy && <button type="button" className="rg-secondary mt-3" onClick={() => void recheck()}>核对签署结果</button>}
      </>}
    </footer>
  </dialog>;
}
